/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip, Typography } from '@mui/material';

import { IOrganizationUser } from '../../../types/organization/IOrganizationUser';
import { ssoIcons } from '../../SsoProviderButton/components/SsoProviderIcons/SsoProviderIcons';

type Props = {
  user: IOrganizationUser['user'];
};

export const UserAuthenticationInfo = memo((props: Props): ReactElement<any> => {
  const { t } = useTranslation();

  switch (props.user.authentication.type) {
    case 'local':
      return (
        <Typography component="p" variant="body2" display="flex" alignItems="center" gap={1}>
          {t('organizationAdmin:management.members_list.authentication.basic_auth')}
        </Typography>
      );
    case 'oauth2':
      return (
        <Typography component="p" variant="body2" display="flex" alignItems="center" gap={1}>
          {t('organizationAdmin:management.members_list.authentication.oauth2')}
          {ssoIcons[props.user.authentication.provider]}
        </Typography>
      );
    case 'oidc':
      return (
        <Typography component="p" variant="body2" display="flex" alignItems="center">
          {t('organizationAdmin:management.members_list.authentication.oidc')}
          <Tooltip
            title={
              <>
                <Typography component="p" variant="body2">
                  {t('organizationAdmin:management.members_list.authentication.part_of_sso_groups')}
                </Typography>
                <ul style={{ marginTop: 4, marginBottom: 4 }}>
                  {props.user.authentication.groups.map(group => (
                    <li key={group}>
                      <Typography component="p" variant="body2" fontFamily="monospace">
                        {group}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </>
            }
          >
            <InfoIcon />
          </Tooltip>
        </Typography>
      );
  }
});
